/**
 * El botón.
 *
 * No existía, y su ausencia se notaba: el mismo botón blanco aparecía con tres radios distintos
 * en tres pantallas —`rounded`, `rounded-lg` y `rounded-xl`— y cada variante estaba copiada a
 * mano allí donde hizo falta.
 *
 * **El borde de 2 px de la variante primaria no es adorno.** Sobre papel, el amarillo del acento
 * da 1,16:1 contra el fondo: sin ese filete de tinta, el botón principal del taller no tendría
 * un límite que cumpla WCAG 1.4.11 y se derretiría en la página.
 */

import { Glifo } from './glifo.js';

export type VarianteBoton = 'primaria' | 'secundaria' | 'fantasma' | 'destructiva';

const VARIANTES: Record<VarianteBoton, string> = {
  primaria: 'bg-hivis text-on-hivis border-edge',
  secundaria: 'border-line text-text hover:bg-surface',
  fantasma: 'border-transparent text-text-dim hover:bg-surface hover:text-text',
  destructiva: 'border-ko text-ko hover:bg-surface',
};

export interface BotonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variante?: VarianteBoton;
  /** Ocupa todo el ancho disponible. */
  readonly ancho?: boolean;
  /** Sustituye el contenido por un indicador de espera sin cambiar de tamaño. */
  readonly cargando?: boolean;
  /**
   * Solo lleva un glifo, sin texto visible.
   *
   * Deja el botón cuadrado en vez de darle el relleno lateral de uno con palabra: tres botones de
   * icono con `px-5` miden 186 px entre los tres para dibujar tres glifos de 18, y en el editor
   * eso desbordaba la fila a 320 px. El área táctil no se pierde: el `min-h-11` fija los 44 px de
   * alto y `aspect-square` los repite a lo ancho.
   *
   * El nombre accesible sigue siendo obligatorio: va en un `<span class="sr-only">` dentro.
   */
  readonly soloIcono?: boolean;
  readonly children: React.ReactNode;
}

export function Boton({
  variante = 'secundaria',
  ancho = false,
  cargando = false,
  soloIcono = false,
  className = '',
  children,
  disabled,
  ...resto
}: BotonProps): React.JSX.Element {
  return (
    <button
      type="button"
      // `min-h-11` son los 44 px de área táctil que exige WCAG 2.5.5, y no se deja al azar del
      // relleno: con un texto de una sola palabra el botón se quedaba en 30 px de alto.
      className={[
        'rounded-chip inline-flex min-h-11 items-center justify-center gap-2 border-2 py-2.5',
        soloIcono ? 'aspect-square min-w-11 px-0' : 'px-5',
        'font-display font-bold',
        'ease-hard transition-[transform,background-color,color] duration-(--dur-tap)',
        'hover:-translate-y-px active:translate-y-px',
        // Deshabilitado se comunica por opacidad Y por el cursor, nunca solo por color.
        'disabled:pointer-events-none disabled:opacity-55',
        ancho ? 'w-full' : '',
        VARIANTES[variante],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={disabled === true || cargando}
      // Mientras carga, quien use un lector de pantalla debe enterarse de que está ocupado.
      aria-busy={cargando || undefined}
      {...resto}
    >
      {cargando ? <Glifo nombre="girando" size={18} className="animate-spin" /> : null}
      {children}
    </button>
  );
}
